import React, { FC, useEffect, useState } from "react";
import { TopLevelPaper } from "../../../../../themes/StyledComponents";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { AllTransactionMethods, recnungTransactionMethods } from "../types/kasse.types";
import useStore from "../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../stores/Transactions/Transactions.provider";
import { useTransaction } from "../hooks/useTransaction";
import { InsuranceBillsPagination } from "../../../Insurance/Base/components/InsuranceBillsPagination";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import TransactionsOverviewTable from "./TransactionsOverviewTable";
import RechnungTabsContainer from "./RechnungTabsContainer";

export const commonStyles = {
  bgcolor: "background.paper",
  border: 0,
  my: 1,
};

interface TransactionsOverviewContainerProps {
  transactionMethod: AllTransactionMethods;
}

const TransactionsOverviewContainer: FC<TransactionsOverviewContainerProps> = ({
  transactionMethod,
}) => {
  const transactionStore = useStore(TransactionsContext);
  const { getAllBankTransfer, getTransactionsByType, lastUpdate } = transactionStore;

  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);

  const accounts = useUserAccounts();

  useEffect(() => {
    if (accounts?.length) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts]);

  const {
    paginatedData,
    allTransaction,
    page,
    setPage,
    limit,
    setLimit,
    searchValue,
    setSearchValue,
    handleStartDateChange,
    handleEndDateChange,
    fetchAllTransaction,
    currentRange,
    refetchTransactions,
  } = useTransaction({
    getAllBankTransfer,
    getTransactionsByType,
    transactionMethod,
    lastUpdate,
    accountId: selectedAccountId,
  });

  // For RECHNUNG, the new RechnungTabsContainer handles everything independently.
  // For other transaction types, render the generic table.
  if (transactionMethod === recnungTransactionMethods.RECHNUNG) {
    return (
      <TopLevelPaper sx={{ borderRadius: "0 0 12px 12px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={3}>
              {accounts && accounts.length > 0 && (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="account-select-label">Mandant</InputLabel>
                  <Select
                    labelId="account-select-label"
                    label="Mandant"
                    value={selectedAccountId || ""}
                    onChange={(e) => setSelectedAccountId(e.target.value as string)}
                  >
                    {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={1}>
              <TextField
                label="Suche nach Transaktionsnummer oder Betrag"
                size="small"
                value={searchValue ?? ""}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{ height: 40, minWidth: 300 }}
              />
            </Grid>
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <DatePicker
                  label="Startdatum"
                  value={currentRange.startDate}
                  onChange={(date) => handleStartDateChange(date)}
                  maxDate={currentRange.endDate || dayjs()}
                  sx={{ "& .MuiInputBase-root": { height: 40 } }}
                />
                <DatePicker
                  label="Enddatum"
                  value={currentRange.endDate}
                  onChange={(date) => handleEndDateChange(date)}
                  minDate={currentRange.startDate || dayjs()}
                  maxDate={dayjs()}
                  sx={{ "& .MuiInputBase-root": { height: 40 } }}
                />
              </Box>
            </Grid>
          </Grid>

          {selectedAccountId && (
            <RechnungTabsContainer
              accountId={selectedAccountId}
              searchValue={searchValue}
              startDate={currentRange.startDate}
              endDate={currentRange.endDate}
            />
          )}
        </LocalizationProvider>
      </TopLevelPaper>
    );
  }

  return (
    <TopLevelPaper sx={{ borderRadius: "0 0 12px 12px" }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container>
          <Grid item xs={3} sx={{ paddingRight: 1 }}>
            {accounts && accounts.length > 0 && (
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="account-select-label">Mandant</InputLabel>
                <Select
                  labelId="account-select-label"
                  label="Mandant"
                  value={selectedAccountId || ""}
                  onChange={(e) => setSelectedAccountId(e.target.value as string)}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <TextField
                label="Suche nach Transaktionsnummer oder Betrag"
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{ height: 40, minWidth: 300 }}
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              sx={{ height: 60 }}
            >
              <DatePicker
                label="Startdatum"
                value={currentRange.startDate}
                defaultValue={dayjs()}
                onChange={(date) => handleStartDateChange(date)}
                maxDate={currentRange.endDate || dayjs()}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
              <DatePicker
                label="Enddatum"
                value={currentRange.endDate}
                defaultValue={dayjs()}
                onChange={(date) => handleEndDateChange(date)}
                minDate={currentRange.startDate || dayjs()}
                maxDate={dayjs()}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {allTransaction.length > 0 ? (
          <TransactionsOverviewTable
            transactions={allTransaction}
            transactionMethod={transactionMethod}
            onRefresh={refetchTransactions}
            accountId={selectedAccountId}
          />
        ) : (
          <Box
            sx={{
              ...commonStyles,
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
            }}
          >
            <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
              <Typography margin={2} variant="subtitle1">
                Für den ausgewählten Status wurden keine Transaktionen gefunden.
              </Typography>
            </TableContainer>
          </Box>
        )}

        {paginatedData && paginatedData.meta.totalItems > 0 && (
          <InsuranceBillsPagination
            totalItems={paginatedData.meta.totalItems}
            totalPages={paginatedData.meta.totalPages}
            limit={limit}
            page={page}
            onPageChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        )}
      </LocalizationProvider>
    </TopLevelPaper>
  );
};

export default TransactionsOverviewContainer;
